import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { BLOG_POSTS } from "@/data/blog-posts";
import { referencedBlogSlugs, gateUnpublishedBlogLinks } from "./blog/link-gate";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false, autoRefreshToken: false } },
  );
}

export type PublicBlogPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  meta_title: string | null;
  meta_description: string | null;
  category: string | null;
  tags: string[] | null;
  body_md: string | null;
  cover_url: string | null;
  cover_alt: string | null;
  hero_image_url: string | null;
  author_name: string | null;
  read_mins: number;
  published_at: string | null;
  updated_at: string;
  /** Last material content edit after publication (title/body/meta). */
  content_updated_at?: string | null;
  cluster_id: string | null;
  article_role: string | null;
};

const SELECT_COLS =
  "id,slug,title,excerpt,meta_title,meta_description,category,tags,body_md,cover_url,cover_alt,hero_image_url,author_name,read_mins,published_at,updated_at,content_updated_at,cluster_id,article_role";

export type RelatedPostInput = Pick<PublicBlogPost, "slug" | "category" | "tags" | "cluster_id">;

/** Stable relevance ordering; recency only breaks otherwise-equal matches. */
export function rankRelatedPosts(source: RelatedPostInput, candidates: PublicBlogPost[]): PublicBlogPost[] {
  const sourceTags = new Set((source.tags ?? []).map((tag) => tag.toLowerCase()));
  return [...candidates]
    .filter((candidate) => candidate.slug !== source.slug)
    .map((candidate) => {
      const sharedTags = (candidate.tags ?? []).filter((tag) => sourceTags.has(tag.toLowerCase())).length;
      const score = (source.cluster_id && candidate.cluster_id === source.cluster_id ? 100 : 0)
        + (source.category && candidate.category === source.category ? 20 : 0)
        + sharedTags * 8;
      return { candidate, score };
    })
    .sort((a, b) => b.score - a.score
      || Date.parse(b.candidate.published_at ?? b.candidate.updated_at) - Date.parse(a.candidate.published_at ?? a.candidate.updated_at)
      || a.candidate.slug.localeCompare(b.candidate.slug))
    .map(({ candidate }) => candidate);
}

export const getPublishedPost = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("blog_posts")
      .select(SELECT_COLS)
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    const post = (row as PublicBlogPost | null) ?? null;
    if (!post?.body_md) return post;
    // Links to sibling articles only become clickable once they are live.
    const slugs = referencedBlogSlugs(post.body_md);
    if (!slugs.length) return post;
    const { data: live } = await supabase
      .from("blog_posts")
      .select("slug")
      .eq("status", "published")
      .in("slug", slugs);
    const liveSet = new Set<string>([
      ...((live ?? []) as Array<{ slug: string }>).map((r) => r.slug),
      ...BLOG_POSTS.map((p) => p.slug),
    ]);
    return { ...post, body_md: gateUnpublishedBlogLinks(post.body_md, liveSet).body_md };
  });

export const getRelatedPosts = createServerFn({ method: "GET" })
  .inputValidator((d) =>
    z
      .object({
        slug: z.string().min(1),
        category: z.string().nullable().optional(),
        tags: z.array(z.string()).nullable().optional(),
        clusterId: z.string().uuid().nullable().optional(),
        excludeSlugs: z.array(z.string()).optional(),
        limit: z.number().int().min(1).max(6).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const limit = data.limit ?? 3;

    const { data: rows, error } = await supabase
      .from("blog_posts")
      .select(SELECT_COLS)
      .eq("status", "published")
      .neq("slug", data.slug)
      .order("published_at", { ascending: false })
      .limit(48);
    if (error) throw new Error(error.message);
    const excluded = new Set(data.excludeSlugs ?? []);
    return rankRelatedPosts(
      { slug: data.slug, category: data.category ?? null, tags: data.tags ?? null, cluster_id: data.clusterId ?? null },
      ((rows as PublicBlogPost[] | null) ?? []).filter((post) => !excluded.has(post.slug)),
    ).slice(0, limit);
  });

export const getPublishedClusterPosts = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ clusterId: z.string().uuid(), slug: z.string().min(1), limit: z.number().int().min(1).max(12).optional() }).parse(d))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: rows, error } = await supabase
      .from("blog_posts")
      .select(SELECT_COLS)
      .eq("status", "published")
      .eq("cluster_id", data.clusterId)
      .neq("slug", data.slug)
      .order("published_at", { ascending: false })
      .limit(data.limit ?? 8);
    if (error) throw new Error(error.message);
    return ((rows as PublicBlogPost[] | null) ?? []).sort((a, b) => {
      if (a.article_role === "pillar" && b.article_role !== "pillar") return -1;
      if (b.article_role === "pillar" && a.article_role !== "pillar") return 1;
      return a.title.localeCompare(b.title);
    });
  });

export const listPublishedPosts = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ limit: z.number().int().min(1).max(50).optional() }).parse(d ?? {}))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: rows, error } = await supabase
      .from("blog_posts")
      .select(SELECT_COLS)
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(data.limit ?? 24);
    if (error) throw new Error(error.message);
    return (rows as PublicBlogPost[] | null) ?? [];
  });
